import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  CLOSING_NOTE_MAX_CHARS,
  COVER_STYLES,
  FOOTER_TEXT_MAX_CHARS,
  PREPARED_BY_MAX_CHARS,
  THEME_KEYS,
  THEME_LOCALES,
  TYPE_PAIRS,
  TYPE_PAIR_FONTS,
  resolveTheme,
  type Theme,
  type ThemeKey,
  type ThemePatch,
  type ThemeView
} from '@tallyframe/core';
import { ReportHeader, themeStyle } from '@tallyframe/renderer';
import { Radio, RadioGroup } from 'react-aria-components';
import { useReports } from '../api/queries';
import { useExportPdf } from '../api/report-queries';
import { useUpdateTheme } from '../api/settings-queries';
import { useBrand } from '../brandContext';
import { saveToDisk } from '../reports/useExport';
import { WorkspaceContext } from '../workspaceContext';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Switch } from '../ui/Checkbox';
import { FieldLabel, fieldClassName, joinClasses, rowClass } from '../ui/Field';
import { InputField, TextareaField } from '../ui/Input';
import { RadioMark } from '../ui/RadioGroup';
import { SelectField } from '../ui/Select';
import { Row, Stack } from '../ui/Stack';
import { ErrorBanner } from './ErrorBanner';

const COVER_LABELS: Record<(typeof COVER_STYLES)[number], string> = {
  classic: 'Classic',
  banner: 'Banner',
  centered: 'Centered'
};
const mutedClass = 'm-0 p-0 font-ui text-xs text-muted';
const SAMPLE_LINE = 'The quick brown fox jumps over the lazy dog';
const PREVIEW_TITLE = 'Weekly Sales Review';
/** A fixed sample, so the locale example never depends on today's date. */
const SAMPLE_NUMBER = 1234567.89;
const SAMPLE_DATE = new Date(Date.UTC(2026, 0, 12));

/** "Changed" means differs from the last seeded value (D-W3). One predicate
 *  drives the keys sent, "Save to preview", and which fields re-seed. */
function changedKeys(draft: Theme, seeded: Theme): ThemeKey[] {
  return THEME_KEYS.filter((k) => draft[k] !== seeded[k]);
}

/** Takes the server's value for every field the operator has not edited since
 *  `base`, and keeps the typed value for the rest. */
function mergeUnedited(current: Theme, base: Theme, next: Theme): Theme {
  const out: Record<string, unknown> = { ...next };
  for (const k of THEME_KEYS) {
    if (current[k] !== base[k]) out[k] = current[k];
  }
  return out as unknown as Theme;
}

function sameTheme(a: Theme, b: Theme): boolean {
  return changedKeys(a, b).length === 0;
}

function localeExample(locale: string): string {
  return `${new Intl.NumberFormat(locale).format(SAMPLE_NUMBER)} · ${new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeZone: 'UTC'
  }).format(SAMPLE_DATE)}`;
}

interface FormState {
  seeded: Theme;
  draft: Theme;
}

/**
 * The "Report theme" section of the Workspace screen (spec §5.6).
 *
 * The form is seeded from `themeView` and then owned by the operator: a later
 * view (a refresh, or this form's own save landing in the app state) only
 * replaces fields they have not edited. A workspace switch discards the form
 * and waits for the new workspace's view, so one workspace's draft never
 * carries into another. `epochRef` ends any save still in flight at that
 * point, or at unmount, so it can no longer re-seed the form.
 */
export function ThemeSettings(): ReactElement {
  const { brand, themeView, beginThemeSave, applySavedTheme } = useBrand();
  const { workspaceId } = useContext(WorkspaceContext);
  const [form, setForm] = useState<FormState | null>(null);

  // Admin mode has no pinned workspace: '' keys its entry and sends no override.
  const reports = useReports(workspaceId ?? '');
  // The server orders by updated_at descending, so the first is the latest.
  const latestReportId = reports.isPending ? undefined : (reports.data?.reports[0]?.id ?? null);
  const updateTheme = useUpdateTheme(workspaceId ?? '');
  const exportPdf = useExportPdf();
  const saving = updateTheme.isPending;
  const exporting = exportPdf.isPending;
  const { reset: resetSave } = updateTheme;

  const epochRef = useRef(0);
  const workspaceRef = useRef(workspaceId);
  // The view that was current when the workspace changed: it belongs to the
  // old workspace, so it must not seed the form.
  const staleViewRef = useRef<ThemeView | null>(null);

  useEffect(
    () => () => {
      epochRef.current += 1;
    },
    []
  );

  useEffect(() => {
    if (workspaceRef.current === workspaceId) return;
    workspaceRef.current = workspaceId;
    epochRef.current += 1;
    staleViewRef.current = themeView;
    setForm(null);
    resetSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a switch matters here
  }, [workspaceId]);

  useEffect(() => {
    if (!themeView) {
      epochRef.current += 1;
      staleViewRef.current = null;
      setForm(null);
      return;
    }
    if (themeView === staleViewRef.current) return;
    staleViewRef.current = null;
    setForm((prev) => {
      if (!prev) return { seeded: themeView.theme, draft: themeView.theme };
      if (sameTheme(prev.seeded, themeView.theme)) return prev;
      return {
        seeded: themeView.theme,
        draft: mergeUnedited(prev.draft, prev.seeded, themeView.theme)
      };
    });
  }, [themeView]);

  const entitled = themeView?.entitled ?? false;
  const draft = form?.draft ?? null;

  const previewBrand = useMemo(() => {
    if (!draft) return undefined;
    return {
      name: brand?.displayName ?? '',
      accent: brand?.accent ?? null,
      logoUrl: brand?.logoUrl ?? null,
      theme: resolveTheme(draft, { customTheme: entitled })
    };
  }, [brand?.displayName, brand?.accent, brand?.logoUrl, draft, entitled]);

  if (!form || !draft || !previewBrand) {
    return (
      <Card title="Report theme">
        <p className={mutedClass}>Loading the report theme…</p>
      </Card>
    );
  }

  const changed = changedKeys(draft, form.seeded);
  const dirty = changed.length > 0;
  const set = <K extends ThemeKey>(key: K, value: Theme[K]) =>
    setForm((prev) => (prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev));

  async function save() {
    if (!form) return;
    const keys = changedKeys(form.draft, form.seeded);
    if (keys.length === 0) return;
    const patch = Object.fromEntries(keys.map((k) => [k, form.draft[k]])) as ThemePatch;
    const sentDraft = form.draft;
    const epoch = epochRef.current;
    const token = beginThemeSave();
    try {
      const result = await updateTheme.mutateAsync(patch);
      // A workspace switch or unmount while the PUT was out: ignore it
      // entirely, for the app theme (its token) and for this form.
      if (epochRef.current !== epoch) return;
      applySavedTheme({ theme: result.theme, entitled: result.entitled }, token);
      // Re-seed only fields not edited since the request was sent.
      setForm((prev) =>
        prev
          ? { seeded: result.theme, draft: mergeUnedited(prev.draft, sentDraft, result.theme) }
          : prev
      );
    } catch {
      // The failure is shown from the mutation state (updateTheme.error).
    }
  }

  async function previewPdf(reportId: string) {
    try {
      saveToDisk(await exportPdf.mutateAsync({ reportId }));
    } catch {
      // The failure is shown from the mutation state (exportPdf.error).
    }
  }

  const locked = !entitled;
  const previewLabel = dirty
    ? 'Save to preview'
    : latestReportId === null
      ? 'Create a report to preview'
      : 'Preview PDF';
  const previewDisabled = dirty || !latestReportId || exporting;

  return (
    <Card title="Report theme">
      <Stack gap={4}>
        {locked ? (
          <p className={`${mutedClass} flex items-center gap-2`}>
            <Badge tone="info">Pro</Badge>
            <span>
              Fonts, cover, block numbers and text fields are part of the Pro plan. The number and date format is
              available on every plan.
            </span>
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-6 m-0 p-0 md:grid-cols-2">
          <Stack gap={4}>
            <RadioGroup
              aria-label="Type pair"
              value={draft.typePair}
              onChange={(key) => set('typePair', key as Theme['typePair'])}
              isDisabled={locked}
              className={fieldClassName()}
            >
              <FieldLabel>Type pair</FieldLabel>
              {TYPE_PAIRS.map((pair) => (
                <Radio key={pair} value={pair} className={joinClasses('group', 'items-start', rowClass)}>
                  <RadioMark className="mt-1" />
                  <span className="flex flex-col gap-1 m-0 p-0">
                    <strong className="m-0 p-0 font-semibold">{TYPE_PAIR_FONTS[pair].label}</strong>
                    <span
                      className="m-0 p-0 text-sm text-muted"
                      data-testid={`pair-sample-${pair}`}
                      style={{ fontFamily: TYPE_PAIR_FONTS[pair].body }}
                    >
                      {SAMPLE_LINE}
                    </span>
                  </span>
                </Radio>
              ))}
            </RadioGroup>

            <RadioGroup
              aria-label="Cover style"
              value={draft.cover}
              onChange={(key) => set('cover', key as Theme['cover'])}
              isDisabled={locked}
              className={fieldClassName()}
            >
              <FieldLabel>Cover style</FieldLabel>
              <Row gap={4}>
                {COVER_STYLES.map((cover) => (
                  <Radio key={cover} value={cover} className={joinClasses('group', rowClass)}>
                    <RadioMark />
                    {COVER_LABELS[cover]}
                  </Radio>
                ))}
              </Row>
            </RadioGroup>

            <Switch
              isSelected={draft.blockNumbers}
              isDisabled={locked}
              onChange={(selected) => set('blockNumbers', selected)}
            >
              Block numbers
            </Switch>

            <InputField
              label="Footer text"
              value={draft.footerText}
              maxLength={FOOTER_TEXT_MAX_CHARS}
              isDisabled={locked}
              onChange={(value) => set('footerText', value)}
            />

            <InputField
              label="Prepared by"
              value={draft.preparedBy}
              maxLength={PREPARED_BY_MAX_CHARS}
              isDisabled={locked}
              onChange={(value) => set('preparedBy', value)}
            />

            <TextareaField
              label="Closing note"
              value={draft.closingNote}
              maxLength={CLOSING_NOTE_MAX_CHARS}
              isDisabled={locked}
              onChange={(value) => set('closingNote', value)}
            />

            <Stack gap={1}>
              <SelectField
                label="Locale"
                value={draft.locale}
                options={THEME_LOCALES.map((locale) => ({ key: locale, label: locale }))}
                onChange={(key) => set('locale', key as Theme['locale'])}
              />
              <span className={mutedClass} data-testid="locale-example">
                {localeExample(draft.locale)}
              </span>
            </Stack>

            <ErrorBanner error={updateTheme.error} />

            <Row gap={2}>
              <Button
                variant="primary"
                isDisabled={!dirty}
                busy={saving}
                busyLabel="Saving…"
                onPress={() => void save()}
              >
                Save theme
              </Button>
            </Row>
          </Stack>

          <Stack gap={4}>
            <h3 className="m-0 p-0 font-ui text-lg font-semibold text-ink">Header preview</h3>
            <div data-testid="theme-preview" style={themeStyle(previewBrand.theme)}>
              <ReportHeader title={PREVIEW_TITLE} preparedFor="Your client" brand={previewBrand} />
            </div>
            <ErrorBanner error={exportPdf.error} />
            <Row gap={2}>
              <Button isDisabled={previewDisabled} onPress={() => latestReportId && void previewPdf(latestReportId)}>
                {previewLabel}
              </Button>
            </Row>
          </Stack>
        </div>
      </Stack>
    </Card>
  );
}
